/** Paid YTD tech earnings above this amount require a 1099 (same rule as API `REQUIRED_1099_THRESHOLD`). */
export const REQUIRED_1099_THRESHOLD = 2000;

export const routes = {
    home: "/",
    controlCenter: "/control-center",
    dashboard: "/dashboard",
    notifications: "/notifications",
    alerts: "/alerts",
    map: "/map",
    calendar: "/calendar",
    comingSoon: "/coming-soon",
    clients: "/clients",
    jobs: "/jobs",
    jobsAll: "/jobs/all",
    jobsActive: "/jobs/active",
    jobsArchived: "/jobs/archived",
    jobsJobDone: "/jobs/job-done",
    jobsPriority: "/jobs/priority",
    jobsDeleted: "/jobs/deleted",
    users: "/users",
    technicians: "/technicians",
    clientRequests: "/client-requests",
    reminders: "/reminders",
    accounting: "/accounting",
    accountingReadyForInvoicing: "/accounting/ready-for-invoicing",
    accountingInvoiced: "/accounting/invoiced",
    accountingInvoicedLoss: "/accounting/invoiced-loss",
    accountingNotQBInvoiced: "/accounting/not-qb-invoiced",
    accountingNotSentToClient: "/accounting/not-sent-to-client",
    accountingFactored: "/accounting/factored",
    accountingNonFactored: "/accounting/non-factored",
    accountingOpenInvoices: "/accounting/open-invoices",
    accountingPaid: "/accounting/paid",
    login: "/login",
    settings: "/settings",
    support: "/support",
    logout: "/logout",
    companies: "/companies",
};

export const api = {
    login: "/auth/login",
    logout: "/auth/logout",
    validateOnBoot: "/auth/validate",
    getClients: "/clients",
    createClient: "/clients",
    updateClient: "/clients",
    acceptClientRequest: "/clients/accept-request",
    rejectClientRequest: "/clients/reject-request",
    suggestClientInvoicePattern: "/clients/invoice-pattern/suggest",
    getClientJobs: "/clients",
    getClientEarningsOverview: "/clients", // GET /clients/:id/earnings-overview
    getClientEarningsJobs: "/clients", // GET /clients/:id/earnings-jobs
    getClientLogs: (clientId) => `/clients/${clientId}/logs`, // GET — owner/GM
    // Client POC API endpoints
    getClientPocs: "/clients",
    createClientPoc: "/clients",
    updateClientPoc: "/clients",
    deleteClientPoc: "/clients",
    getJobs: "/jobs",
    getJobsArAp: "/jobs/ar-ap",
    getJobById: "/jobs",
    createJob: "/jobs",
    updateJob: "/jobs",
    generateJobDescription: "/jobs/generate-description",
    generateProposalContent: "/jobs/generate-proposal",
    getAiPromptTemplates: "/jobs/ai-prompt-templates",
    createAiPromptTemplate: "/jobs/ai-prompt-templates",
    updateAiPromptTemplate: "/jobs/ai-prompt-templates",
    setDefaultAiPromptTemplate: "/jobs/ai-prompt-templates",
    deleteAiPromptTemplate: "/jobs/ai-prompt-templates",
    deleteJob: "/jobs",
    getDeletedJobs: "/jobs/deleted",
    getDeletedJobById: "/jobs/deleted",
    getStoreNames: "/jobs/store-names",
    createStoreName: "/jobs/store-names",
    updateStoreName: "/jobs/store-names",
    deleteStoreName: "/jobs/store-names",
    getJobTags: "/jobs/tags",
    createJobTag: "/jobs/tags",
    updateJobTag: "/jobs/tags",
    deleteJobTag: "/jobs/tags",
    getUsers: "/users",
    getUserById: (userId) => `/users/${userId}`,  // GET company-scoped member
    createUser: "/users",
    updateUser: "/users",
    updateUserStatus: (userId) => `/users/${userId}/status`,
    updateMyProfile: "/users/me",                 // PATCH
    getUserLogs: (userId) => `/users/${userId}/logs`, // GET
    uploadMyAvatar: "/users/me/avatar",           // POST
    deleteMyAvatar: "/users/me/avatar",           // DELETE
    getMyAvatarDownloadUrl: "/users/me/avatar/download", // GET (self)
    getUserAvatarDownloadUrl: (userId) => `/users/${userId}/avatar/download`, // GET
    createTechnician: "/technicians",
    updateTechnician: "/technicians",
    getTechnicianLogs: (technicianId) => `/technicians/${technicianId}/logs`, // GET — owner/GM/admin
    uploadJobFiles: "/uploads/direct",
    uploadAttachment: "/uploads/direct",
    getUploadPresignedUrl: "/uploads/presigned-url", // POST - large files (videos) upload direct to S3
    confirmUpload: "/uploads/confirm",               // POST - create the attachment row after a presigned upload
    deleteFile: "/uploads",
    deleteAttachment: "/uploads",
    getAttachmentDownloadUrl: "/uploads/download",
    getDownloadUrl: "/uploads/download",
    updateAttachmentCategory: "/uploads",
    updateAttachmentPrivacy: "/uploads",
    deploy: "/deploy",
    lastCommit: "/deploy/last-commit",
    serverStats: "/deploy/server-stats",
    getMigrations: "/deploy/migrations",
    runMigrations: "/deploy/migrations/run",
    // Parts API endpoints
    createPart: "/jobs",
    updatePart: "/jobs",
    deletePart: "/jobs",
    approvePart: "/jobs",
    lockPart: "/jobs",
    unlockPart: "/jobs",
    requestPartApproval: "/jobs",
    requestPartUnlock: "/jobs",
    rejectPart: "/jobs",
    // Proposals API endpoints
    invoiceProposal: "/jobs",
    uninvoiceProposal: "/jobs",
    // Comments API endpoints
    getJobComments: "/jobs",
    createJobComment: "/jobs",
    updateJobComment: "/jobs",
    getJobLogs: "/jobs",
    // Technician attachments API endpoints
    uploadTechnicianAttachment: "/technicians",
    getTechnicianAttachments: "/technicians",
    deleteTechnicianAttachment: "/technicians",
    // Technician earnings by year
    getTechniciansEarningsByYear: "/technicians/earnings-by-year",
    // Technician earnings by date range
    getTechnicianEarningsByDateRange: "/technicians",
    // Job Technicians API endpoints
    createJobTechnician: "/jobs",    // POST /jobs/:jobId/technicians
    updateJobTechnician: "/jobs",    // PUT /jobs/:jobId/technicians/:id
    deleteJobTechnician: "/jobs",    // DELETE /jobs/:jobId/technicians/:id
    lockJobTechnician: "/jobs",      // POST /jobs/:jobId/technicians/:id/lock
    unlockJobTechnician: "/jobs",    // POST /jobs/:jobId/technicians/:id/unlock
    requestUnlockJobTechnician: "/jobs", // POST /jobs/:jobId/technicians/:id/request-unlock
    // Job Technician Attachments
    getJobTechnicianAttachments: "/jobs",    // GET /jobs/:jobId/technicians/:id/attachments
    uploadJobTechnicianAttachment: "/jobs",  // POST /jobs/:jobId/technicians/:id/attachments
    deleteJobTechnicianAttachment: "/jobs",  // DELETE /jobs/:jobId/technicians/:id/attachments/:attachmentId
    // Notifications API endpoints
    getNotifications: "/notifications",       // GET /notifications
    readNotification: "/notifications",       // POST /notifications/:id/read
    readAllNotifications: "/notifications/read-all", // POST /notifications/read-all
    archiveNotification: "/notifications",    // POST /notifications/:id/archive
    pushOwnerNotification: "/notifications/push", // POST /notifications/push (owner only)
    notificationPreferences: "/notifications/preferences", // GET/PUT /notifications/preferences
    // Owner alerts (scheduled digests, mailing lists)
    getAlertsMeta: "/alerts/meta",
    getAlerts: "/alerts",
    createAlert: "/alerts",
    updateAlert: "/alerts",
    deleteAlert: "/alerts",
    testAlert: "/alerts",
    getAlertGroups: "/alerts/groups",
    createAlertGroup: "/alerts/groups",
    updateAlertGroup: "/alerts/groups",
    deleteAlertGroup: "/alerts/groups",
    // Job financial fields endpoints (owner, accountant, general_manager only)
    updateQuickbooksInvoiced: "/jobs",        // PATCH /jobs/:id/quickbooks-invoiced
    updateFactored: "/jobs",                  // PATCH /jobs/:id/factored
    updatePaid: "/jobs",                      // PATCH /jobs/:id/paid
    updateSentToClient: "/jobs",              // PATCH /jobs/:id/sent-to-client
    updateJobNotes: "/jobs",                 // PATCH /jobs/:id/notes
    createJobLink: "/jobs",                 // POST /jobs/:id/links
    updateJobLink: "/jobs",                 // PUT /jobs/:id/links/:linkId
    deleteJobLink: "/jobs",                 // DELETE /jobs/:id/links/:linkId
    // Job locking endpoints
    unlockJob: "/jobs",                     // PATCH /jobs/:id/unlock (owner / general_manager)
    requestUnlockJob: "/jobs",              // POST /jobs/:id/request-unlock (admin)
    dismissUnlockRequest: "/jobs",          // POST /jobs/:id/dismiss-unlock-request (owner / general_manager)
    getAccountingJobs: "/accounting/jobs",    // GET /accounting/jobs?view=...
    getAccountingJobCounts: "/accounting/job-counts", // GET /accounting/job-counts
    // Dashboard API endpoints
    getDashboardMetrics: "/dashboard/metrics",           // GET /dashboard/metrics
    getFilteredDashboardMetrics: "/dashboard/filtered-metrics",  // GET /dashboard/filtered-metrics
    getFilteredJobs: "/dashboard/filtered-jobs",         // GET /dashboard/filtered-jobs
    // Control Center API endpoints (owner / GM / accountant / admin)
    getControlCenterOverview: "/control-center/overview",
    getControlCenterJobs: "/control-center/jobs",
    getControlCenterTechnicians: "/control-center/technicians",
    getControlCenterActivity: "/control-center/activity",
    getControlCenterTopClients: "/control-center/top-clients",
    getControlCenterTopDispatchers: "/control-center/top-dispatchers",
    // Platform / super-admin runtime config (super_admin only)
    getPlatformConfig: "/config",               // GET /config
    updatePlatformConfig: "/config",            // PATCH /config { enableQueryLogs?: boolean, ... }
    // Platform / super-admin company management (super_admin only)
    getCompanies: "/companies",                 // GET /companies
    createCompany: "/companies",                // POST /companies { name }
    provisionCompany: "/companies/provision",   // POST /companies/provision { company_name, first_name, ... }
    updateCompany: "/companies",                // PATCH /companies/:companyId { name }
    addCompanyOwner: "/companies",              // POST /companies/:companyId/owners
    getCompanyMembers: "/companies",            // GET /companies/:companyId/members
    getEligibleCompanyUsers: "/companies",      // GET /companies/:companyId/eligible-users
    getDeactivatedUsers: "/companies/deactivated-users", // GET /companies/deactivated-users
    addCompanyMember: "/companies",             // POST /companies/:companyId/members
    updateCompanyMember: "/companies",          // PATCH /companies/:companyId/members/:userId
    removeCompanyMember: "/companies",          // DELETE /companies/:companyId/members/:userId
    // Multi-company membership (regular users)
    myCompanies: "/auth/my-companies",          // GET
    switchCompany: "/auth/switch-company",      // POST { company_id }
    // Company profile (tenant-scoped)
    getCompanyProfile: "/company/profile",              // GET
    updateCompanyProfile: "/company/profile",           // PATCH
    getCompanyInvoiceBranding: "/company/invoice-branding", // GET
    uploadCompanyLogo: "/company/logo",                 // POST
    deleteCompanyLogo: "/company/logo",                 // DELETE
    getCompanyAttachments: "/company/attachments",      // GET
    uploadCompanyAttachment: "/company/attachments",    // POST
    updateCompanyAttachment: "/company/attachments",    // PATCH /company/attachments/:id
    deleteCompanyAttachment: "/company/attachments",    // DELETE /company/attachments/:id
    downloadCompanyAttachment: "/company/attachments",  // GET /company/attachments/:id/download
};

// States code
export const states = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    AS: 'American Samoa',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    GU: 'Guam',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    MP: 'Northern Mariana Islands',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    PR: 'Puerto Rico',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    TT: 'Trust Territories',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    VI: 'Virgin Islands',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};